import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Typography, Chip } from "@mui/material";

import messages from "../../../../assets/locale/messages";
import emptyBoxImg from "../../../../assets/images/placeholders/emptyBox.svg";
import {
  getBankTransferVerificationsRequestDetailsRequest,
  getBankTransferVerificationsRequestDetailsResponse,
  getOffPlatformVerificationsRequestDetailsRequest,
  getOffPlatformVerificationsRequestDetailsResponse,
  getOnlinePaymentVerificationsRequestDetailsRequest,
  getOnlinePaymentVerificationsRequestDetailsResponse,
  getVerificationsRequestMainInfo
} from "../../../../store/BusinessVerification/actions";
import {
  BankSmallIcon,
  CardArrowSmallIcon,
  DocumentsSmallIcon
} from "../../../../utils/Icons";
import {
  COMPANY_VERIFICATION_STATUS,
  CHIPS_CLASSES,
  VERIFICATION_METHODS,
  BUSINESS_VERIFICATION_STATUS_LOCALE_KEYS
} from "../../../../utils/Constants";
import OnlinePaymentRequest from "./OnlinePaymentRequest";
import BankTransferRequest from "./BankTransferRequest";
import OffPlatformRequest from "./OffPlatformRequest";
import EmptyState from "../../../../components/EmptyState";
import ApproveDocuments from "./components/ActionButtons/ApproveDocuments";
import RequestUpdates from "./components/ActionButtons/RequestUpdates";
import ApproveRequest from "./components/ActionButtons/ApproveRequest";
import RejectRequest from "./components/ActionButtons/RejectRequest";
import {
  hasPermission,
  PERMISSIONS_IDS
} from "../../../../utils/Roles";

const ViewVerificationRequestDetails = () => {
  const dispatch = useDispatch();
  const { id, type } = useParams();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { businessVerification } = messages[lang];

  const requestDetails = useSelector(
    (state) => state.businessVerification.verificationRequestMainInfo
  );

  useEffect(() => {
    dispatch(
      type == VERIFICATION_METHODS.onlinePayment
        ? getOnlinePaymentVerificationsRequestDetailsRequest({ id })
        : type == VERIFICATION_METHODS.bankTransfer
          ? getBankTransferVerificationsRequestDetailsRequest({ id })
          : getOffPlatformVerificationsRequestDetailsRequest({ id })
    );
    return () => {
      dispatch(getVerificationsRequestMainInfo({}));
      dispatch(
        getOnlinePaymentVerificationsRequestDetailsResponse({})
      );
      dispatch(getOffPlatformVerificationsRequestDetailsResponse({}));
      dispatch(
        getBankTransferVerificationsRequestDetailsResponse({})
      );
    };
  }, [type, id]);

  const chipsStatusClass = (status) => {
    let chipClass = "";
    switch (status) {
      case COMPANY_VERIFICATION_STATUS.awaitingRequiredDocuments:
        chipClass = CHIPS_CLASSES.blue;
        break;
      case COMPANY_VERIFICATION_STATUS.pendingDocumentsUpload:
        chipClass = CHIPS_CLASSES.pink;
        break;
      case COMPANY_VERIFICATION_STATUS.documentsUploaded:
        chipClass = CHIPS_CLASSES.darkBlue;
        break;
      case COMPANY_VERIFICATION_STATUS.pendingPayment:
        chipClass = CHIPS_CLASSES.purple;
        break;
      case COMPANY_VERIFICATION_STATUS.docsNeedUpdates:
        chipClass = CHIPS_CLASSES.darkYellow;
        break;
      case COMPANY_VERIFICATION_STATUS.pendingFinalApproval:
        chipClass = CHIPS_CLASSES.orange;
        break;
      case COMPANY_VERIFICATION_STATUS.rejected:
        chipClass = CHIPS_CLASSES.red;
        break;
      case COMPANY_VERIFICATION_STATUS.approved:
        chipClass = CHIPS_CLASSES.green;
        break;
      default:
        break;
    }
    return chipClass;
  };

  return (
    <div className="verification-request-details-container">
      <>
        {requestDetails && !!Object.keys(requestDetails).length ? (
          <>
            <div className="page-title mb-4 d-flex justify-content-between align-items-start align-items-md-center flex-column flex-md-row">
              <Typography
                variant="h5"
                className="title-color me-3 mb-2 mb-md-0">
                {businessVerification.requestDetails.title}
              </Typography>
              <div className="request-actions-container d-flex justify-content-start justify-content-md-end  align-items-start align-items-md-center flex-wrap">
                {(hasPermission(
                  PERMISSIONS_IDS.verification_request_offplatform_final_accept_reject
                ) ||
                  hasPermission(
                    PERMISSIONS_IDS.verification_request_online_final_accept_reject
                  ) ||
                  hasPermission(
                    PERMISSIONS_IDS.verification_request_banktransfer_final_accept_reject
                  )) && (
                  <>
                    {[
                      COMPANY_VERIFICATION_STATUS.documentsUploaded,
                      COMPANY_VERIFICATION_STATUS.pendingFinalApproval
                    ].includes(requestDetails?.RequestStatusId) && (
                      <div className="me-2 mb-md-0 mb-2">
                        <RejectRequest
                          requestType={
                            type == VERIFICATION_METHODS.onlinePayment
                              ? VERIFICATION_METHODS.onlinePayment
                              : type ==
                                  VERIFICATION_METHODS.bankTransfer
                                ? VERIFICATION_METHODS.bankTransfer
                                : VERIFICATION_METHODS.offPlatform
                          }
                        />
                      </div>
                    )}
                  </>
                )}
                {hasPermission(
                  PERMISSIONS_IDS.verification_request_offplatform_initial_accept_reject
                ) && (
                  <>
                    {type == VERIFICATION_METHODS.offPlatform &&
                      requestDetails?.RequestStatusId ===
                        COMPANY_VERIFICATION_STATUS.documentsUploaded && (
                        <>
                          <div className="me-2 mb-md-0 mb-2">
                            <RequestUpdates />
                          </div>
                          <div>
                            <ApproveDocuments />
                          </div>
                        </>
                      )}
                  </>
                )}
                {(hasPermission(
                  PERMISSIONS_IDS.verification_request_offplatform_final_accept_reject
                ) ||
                  hasPermission(
                    PERMISSIONS_IDS.verification_request_online_final_accept_reject
                  ) ||
                  hasPermission(
                    PERMISSIONS_IDS.verification_request_banktransfer_final_accept_reject
                  )) && (
                  <>
                    {requestDetails?.RequestStatusId ===
                      COMPANY_VERIFICATION_STATUS.pendingFinalApproval && (
                      <div>
                        <ApproveRequest
                          requestType={
                            type == VERIFICATION_METHODS.onlinePayment
                              ? VERIFICATION_METHODS.onlinePayment
                              : type ==
                                  VERIFICATION_METHODS.bankTransfer
                                ? VERIFICATION_METHODS.bankTransfer
                                : VERIFICATION_METHODS.offPlatform
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="request-main-info d-flex align-items-start align-items-md-center justify-content-start flex-column flex-md-row mb-4">
              <div className="d-flex align-items-start align-items-md-center flex-md-row flex-column mb-md-0 mb-3">
                <p className="me-3 text-color mb-md-0 mb-2">
                  {
                    businessVerification.requestDetails
                      .verificationMethod
                  }
                  :
                </p>
                <p className="mb-0">
                  {type == VERIFICATION_METHODS.offPlatform && (
                    <DocumentsSmallIcon />
                  )}
                  {type == VERIFICATION_METHODS.onlinePayment && (
                    <CardArrowSmallIcon />
                  )}
                  {type == VERIFICATION_METHODS.bankTransfer && (
                    <BankSmallIcon />
                  )}
                  <span className="ms-2 text-color fweight-600 fsize-16">
                    {type == VERIFICATION_METHODS.offPlatform &&
                      businessVerification.verificationMethods
                        .offPlatform}
                    {type == VERIFICATION_METHODS.onlinePayment &&
                      businessVerification.verificationMethods
                        .onlinePayment}
                    {type == VERIFICATION_METHODS.bankTransfer &&
                      businessVerification.verificationMethods
                        .bankTransfer}
                  </span>
                </p>
              </div>
              <Box
                sx={{
                  width: {
                    xs: "0",
                    md: "2px"
                  },
                  height: {
                    xs: "0",
                    md: "25px"
                  },
                  backgroundColor: "#C3C3C3",
                  margin: "0 1rem"
                }}></Box>
              <div>
                <span className="me-3 text-color">
                  {businessVerification.requestDetails.requestStatus}:
                </span>
                <Chip
                  label={
                    <span className="fsize-14">
                      {
                        businessVerification.inprogressRequestStatus[
                          BUSINESS_VERIFICATION_STATUS_LOCALE_KEYS[
                            requestDetails?.RequestStatusId
                          ]
                        ]
                      }
                    </span>
                  }
                  className={`verification-request-chip mt-md-0 mt-2 ${chipsStatusClass(requestDetails?.RequestStatusId)}`}
                />
              </div>
            </div>
            {type == VERIFICATION_METHODS.onlinePayment && (
              <OnlinePaymentRequest />
            )}
            {type == VERIFICATION_METHODS.bankTransfer && (
              <BankTransferRequest />
            )}
            {type == VERIFICATION_METHODS.offPlatform && (
              <OffPlatformRequest />
            )}
          </>
        ) : (
          !isLoading && (
            <EmptyState
              subTitle={businessVerification.requestDetails.noData}
              imgSrc={emptyBoxImg}
            />
          )
        )}
      </>
    </div>
  );
};

export default ViewVerificationRequestDetails;
